import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector, batch } from '@tmapy/redux';
import { useOnLine, useThrottle } from '@tmapy/beetlejuice-utils';

import type { PrejimkaTypesEnum } from '../openapi/types/PrejimkaTypesEnum';
import { useSseNotificationsTokenCreateRetrieveResource } from '../openapi/operations/sseNotificationsTokenCreateRetrieve';

import { actionResourcePatchSSE } from '../redux/resource/actions';
import { loadDoc } from '../docs/actions/docDetail';

export const SSE_TYPE = {
  DOCS: 'dmx:docs',
  CHECKS: 'dmx:docchecks',
  ORDERS: 'dmx:orders',
  SVU: 'dmx:urceny_editor_zps',
  EPSI: 'dmx:epsi_docs',
};

export const DOCS_PATH: Record<PrejimkaTypesEnum, string> = {
  GP: 'docs',
  DTM: 'docs',
  INITIAL_IMPORT: 'docs',
  CHECK: 'docs',
  DMVS_GAD_CHECK: 'docs',
  DMVS_DTI: 'docs',
  DMVS_GAD: 'dmvs_gad_docs',
  CEPRO_IMPORT: 'docs',
  BB_IMPORT: 'docs',
  DTM_KAD: 'docs',
};

/**
 * `onEvent` se volá při každé příchozí zprávě odebíraného kanálu. Na rozdíl od
 * `incomingSSEEventType` funguje i pro opakované zprávy stejného typu - ten totiž
 * nabývá pořád stejné hodnoty, takže druhá a další zpráva už závislost effectu
 * ve volající komponentě nezmění.
 *
 * Kanály jsou sdílené a chodí po nich i cizí objekty, proto kanály nesoucí `id`
 * volají `onEvent` jen pro `docId`; kanál EPSI ho nemá a volá ho vždy.
 */
export const useSSE = (subscription: string, docId?: number, onEvent?: () => void) => {
  const onLine = useOnLine();
  const [throttled, throttle] = useThrottle();
  const dispatch = useDispatch();
  const server = useSelector((state) => state.app.config.server);
  const sseToken = useSseNotificationsTokenCreateRetrieveResource({});

  const [incomingSSEEventType, setIncomingSSEEventType] = useState<string | null>(null);

  // Callback drží ref, aby jeho identita neovlivňovala `channelHandlers` a přes ně
  // ani hlavní effect - jinak by každý nestabilní callback znamenal nové připojení.
  const onEventRef = useRef(onEvent);
  useEffect(() => {
    onEventRef.current = onEvent;
  });

  const docIdRef = useRef(docId);
  useEffect(() => {
    docIdRef.current = docId;
  });

  const channelHandlers: Record<string, (pub: any) => void> = useMemo(
    () => ({
      [SSE_TYPE.DOCS]: (pub) => {
        const { id, state, prejimka_type } = pub.data;
        const url = `${server}/dmx-server/${DOCS_PATH[prejimka_type as PrejimkaTypesEnum]}/`;
        batch('SSE DOCS channelHandlers', () => {
          dispatch(actionResourcePatchSSE({ url, id, type: SSE_TYPE.DOCS, data: { state } }));
          // force attachments update
          if (id === docIdRef.current) {
            dispatch(loadDoc(id));
            onEventRef.current?.();
          }
          setIncomingSSEEventType(SSE_TYPE.DOCS);
        });
      },
      [SSE_TYPE.CHECKS]: (pub) => {
        const { id, state, doc_id } = pub.data;
        const url = `${server}/dmx-server/${DOCS_PATH['CHECK']}/`;
        batch('SSE CHECKS channelHandlers', () => {
          dispatch(
            actionResourcePatchSSE({
              url,
              id: doc_id,
              type: SSE_TYPE.CHECKS,
              data: { id, state },
            }),
          );
          // force attachments update
          if (doc_id === docIdRef.current) {
            dispatch(loadDoc(doc_id));
            onEventRef.current?.();
          }
          setIncomingSSEEventType(SSE_TYPE.CHECKS);
        });
      },
      [SSE_TYPE.ORDERS]: (pub) => {
        const { id, state } = pub.data;
        const url = `${server}/dmx-server/orders/`;
        dispatch(actionResourcePatchSSE({ url, id, type: SSE_TYPE.ORDERS, data: { state } }));
        setIncomingSSEEventType(SSE_TYPE.ORDERS);
      },
      /**
       * Payload je celý řádek listingu, ale nové dny vznikají za běhu a
       * `actionResourcePatchSSE` umí jen přepsat už načtený řádek — přehled se
       * proto obnovuje refetchem.
       */
      [SSE_TYPE.EPSI]: () => {
        setIncomingSSEEventType(SSE_TYPE.EPSI);
        onEventRef.current?.();
      },
      [SSE_TYPE.SVU]: (pub) => {
        const { id, state } = pub.data;
        const url = `${server}/dmx-server/r7_urceni_editora_zps/`;
        batch('SSE SVU channelHandlers', () => {
          dispatch(
            actionResourcePatchSSE({
              url,
              id,
              type: SSE_TYPE.SVU,
              data: { id, state },
            }),
          );
          if (id === docIdRef.current) {
            onEventRef.current?.();
          }
          setIncomingSSEEventType(SSE_TYPE.SVU);
        });
      },
    }),
    [server, dispatch],
  );

  useEffect(() => {
    const token = sseToken.data?.sse_notification_token;
    if (!token || !onLine || throttled) return;

    const url = new URL('/notifications/connection/uni_sse', new URL(server, window.location.href));
    const subs = { [subscription]: {} };
    url.searchParams.append(
      'cf_connect',
      JSON.stringify({
        token,
        subs,
      }),
    );

    console.debug(`[SSE] Subscribing`, subs);
    const eventSource = new EventSource(url);
    eventSource.onmessage = (event) => {
      console.info(`[SSE] Data`, event.data);
      const { channel, pub, connect, disconnect } = JSON.parse(event.data);
      if (connect) {
        // TODO: handle expires, ttl, ...
      } else if (disconnect) {
        // this will cause reconnect
        sseToken.refetch();
      } else {
        // TODO: handle ping
        // SSE can become stale (like connected but really withot live data channel)

        if (channel) {
          channelHandlers[channel]?.(pub);
        }
      }
    };

    eventSource.onerror = (event) => {
      console.warn(`[SSE] Error:`, event);
      // if error, wait from 5 to 10 seconds
      throttle(5000 + 5000 * Math.random());
      sseToken.refetch();
    };

    return () => {
      console.debug(`[SSE] Unsubscribing`, subs);
      eventSource.onmessage = null;
      eventSource.onerror = null;
      eventSource.close();
    };
  }, [channelHandlers, onLine, server, sseToken, throttle, throttled]);

  return { incomingSSEEventType };
};
